import React from 'react'
import styled from '@emotion/styled'
import { Locale } from 'locale'
import { Growl, NotificationType } from 'store/notification'
import { iconSet } from 'view/components/icons/iconSet'
import * as constants from 'view/styles/constants'
import { MultiButton } from 'view/components/buttons'

export interface GrowlItemProps extends Growl {
  onDismiss: (id: string) => unknown
}

const GrowlItemRoot = styled.li<{ type: NotificationType }>`
  font-size: 12px;
  min-height: 44px;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 12px;
  color: ${constants.CORE_PRIMARY};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  overflow: hidden;
  cursor: pointer;
  border-bottom-width: 4px;
  border-bottom-style: solid;
  border-bottom-color: ${props => getNotificationColor(props.type)};
  background-color: rgba(64, 64, 64, 0.9);
  &:hover {
    background-color: rgba(64, 64, 64, 1);
  }
`

const GrowlIcon = styled.span`
  flex: 0 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  border: 2px solid ${constants.CORE_PRIMARY};
  border-radius: 50%;
  svg {
    height: 9px;
    width: 9px;
  }
`

const GrowlMessage = styled.p`
  flex: 1 1 auto;
  margin: 6px 9px;
  word-wrap: break-word;
  white-space: pre-line;
  user-select: none;
  overflow: hidden;
`

const CloseButton = styled(MultiButton)`
  flex: 0 0 20px;
  height: 20px;
  border-radius: 50%;
  color: inherit;
  &:hover {
    background-color: ${constants.CORE_PRIMARY_5};
  }
  svg {
    height: 11px;
    width: 11px;
  }
`

function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case NotificationType.Success:
      return iconSet.tickIcon
    case NotificationType.Error:
      return iconSet.crossIcon // TODO
    default:
      throw new Error(`Unhandled NotificationType [${type}]`)
  }
}

function getNotificationColor(type: NotificationType) {
  switch (type) {
    case NotificationType.Success:
      return constants.ACCENT_GOOD_1
    case NotificationType.Error:
      return constants.ACCENT_BAD_1
    default:
      throw new Error(`Unhandled NotificationType [${type}]`)
  }
}

export class GrowlItem extends React.PureComponent<GrowlItemProps> {
  handleDismiss = () => {
    this.props.onDismiss(this.props.id)
  }

  render() {
    const { type } = this.props

    return (
      <GrowlItemRoot type={type} onClick={this.handleDismiss}>
        <GrowlIcon>{getNotificationIcon(type)}</GrowlIcon>
        <GrowlMessage>{this.getFormattedMessage()}</GrowlMessage>
        <CloseButton onClick={this.handleDismiss}>{iconSet.crossIcon}</CloseButton>
      </GrowlItemRoot>
    )
  }

  getTitle(): string {
    const { title, titleText } = this.props

    if (titleText) {
      return titleText
    } else if (title) {
      return Locale.translate(title)
    } else {
      throw new Error('Either titleText or title must be specified')
    }
  }

  getFormattedMessage(): string {
    const { message, messageArgs, messageText } = this.props

    if (messageText) {
      return messageText
    } else if (message) {
      return Locale.translate(message, messageArgs)
    } else {
      throw new Error('Either messageText or message must be specified')
    }
  }
}
